import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, ButtonBase, CircularProgress, Divider, IconButton, InputAdornment, Stack,
  TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAgentChat } from 'src/hooks/useAgentChat';
import { useCopiloto } from 'src/contexts/copiloto-context';
import TarjetaTema from 'src/components/controlObra/TarjetaTema';
import { conSeparadoresDeDia } from 'src/utils/separadoresDeDia';
import { plegarHilo } from 'src/utils/plegadoDelHilo';
import TarjetasDelCopiloto from 'src/components/copiloto/TarjetasDelCopiloto';
import { aplicoCambios, esTarjetaVigente, tarjetasDelTurno, vaAlTema } from 'src/components/copiloto/logicaTarjetas';

/**
 * El copiloto: una sola conversación.
 *
 * Antes esto eran dos solapas —"Qué falta" y "Preguntar"— y esa división era mía,
 * no de quien la usa: la cola escribe y el chat sólo lee. Del lado de la persona
 * es un mismo diálogo, y partirlo la obligaba a adivinar a cuál de las dos
 * mitades del copiloto le tocaba hablar.
 *
 * Entonces hay un hilo con dos tipos de turno: texto, y propuestas que se
 * dibujan como tarjeta adentro del hilo. La tarjeta conserva su estructura
 * porque ahí están las dos cosas que no pueden pasar por el modelo: los números
 * —los calcula el código— y el sí —lo da la persona—.
 *
 * El input es uno solo. Si hay un tema abierto, el mensaje va primero al
 * intérprete; si no era una respuesta a esa pregunta, cae al chat de siempre,
 * que no escribe nada. El modo de falla es benigno a propósito.
 *
 * La conversación es una sola por usuario y no se corta por obra, así que el
 * historial que se abre puede tener semanas y varias obras. Por eso el hilo abre
 * parado en lo último —el principio de algo de hace un mes no es donde estás—,
 * cada cambio de día lleva su separador —sin fecha, turnos de semanas distintas
 * se leen como si fueran de la misma tarde— y a la vista queda sólo lo de las
 * últimas 24 horas de esta obra: el resto espera atrás de «Ver mensajes previos».
 *
 * Viaja con lo que la pantalla declaró —la obra abierta, si hay una—, así que no
 * hay que nombrársela: ni puede confundirse de obra, que sería el peor error
 * posible hablando de plata.
 */

const SUGERENCIAS = [
  '¿Cómo viene la obra?',
  '¿Hace cuánto no cargamos avance?',
];

export default function PanelCopiloto() {
  const { contexto, abierto, cerrar, q, avisarAplicado } = useCopiloto();
  const { messages, isSending, error, sendMessage, loadHistory, hasLoadedHistory } = useAgentChat({ contexto });
  const obraAbierta = contexto?.control_obra_id || null;

  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  // El hilo lo arma el panel: los turnos del agente entran acá igual que los del
  // copiloto contestando un tema, para que la conversación sea UNA.
  const [hilo, setHilo] = useState([]);
  const [desplegado, setDesplegado] = useState(false);
  const foldRef = useRef({ vistos: 0, sembrado: false });
  const finRef = useRef(null);
  const cajaRef = useRef(null);
  const aterrizadoRef = useRef(false);
  const alFondoRef = useRef(null);
  const montadoRef = useRef(Date.now());

  useEffect(() => { if (abierto && !hasLoadedHistory) loadHistory(); }, [abierto, hasLoadedHistory, loadHistory]);

  // Los mensajes del agente se pliegan al hilo. Los del usuario los escribo yo
  // al enviarlos —también cuando el mensaje va al intérprete y no al agente—,
  // así que del historial se siembra todo una vez y después sólo lo que contesta.
  useEffect(() => {
    const f = foldRef.current;
    if (messages.length <= f.vistos) return;
    const nuevos = messages.slice(f.vistos);
    f.vistos = messages.length;
    const turnos = nuevos
      .filter((m) => f.sembrado === false || m.role !== 'user')
      .map((m, i) => ({
        id: `ag-${f.vistos}-${i}`,
        rol: m.role,
        texto: m.content,
        fecha: m.createdAt || null,
        obra: m.control_obra_id || null,
        tarjetas: m.role === 'assistant' ? tarjetasDelTurno(m.actions) : [],
        delAgente: true,
        enVivo: Date.parse(m.createdAt) >= montadoRef.current,
      }));
    const yaEstaba = f.sembrado;
    f.sembrado = true;
    if (turnos.length) setHilo((h) => [...h, ...turnos]);
    // El copiloto puede haber propuesto algo en ese turno. La cola se relee para
    // que el tema aparezca solo, sin que la persona tenga que buscarlo.
    if (yaEstaba && nuevos.some((m) => m.role === 'assistant')) q.refrescarCola();
    // Un lote aplicado cambió la obra: lo que hay en pantalla quedó viejo.
    if (yaEstaba && nuevos.some((m) => aplicoCambios(m.actions))) avisarAplicado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Al abrir hay que caer en lo último, no en lo más viejo. El historial llega
  // asíncrono, así que el aterrizaje es el primer render con turnos de esta
  // apertura y va de golpe —animar un mes de scroll es marear—; de ahí en más se
  // sigue a los turnos nuevos con la animación de siempre.
  useEffect(() => {
    if (!abierto) { aterrizadoRef.current = false; return; }
    if (aterrizadoRef.current) { finRef.current?.scrollIntoView({ behavior: 'smooth' }); return; }
    const caja = cajaRef.current;
    if (!caja) return;
    caja.scrollTop = caja.scrollHeight;
    if (hilo.length) aterrizadoRef.current = true;
  }, [abierto, hilo.length, isSending, pensando, q.tema]);

  // Cambiar de obra vuelve a plegar: lo desplegado era el historial de la otra.
  useEffect(() => { setDesplegado(false); }, [obraAbierta]);

  const { visibles, ocultos } = useMemo(
    () => plegarHilo(hilo, { obra: obraAbierta, desplegado }),
    [hilo, obraAbierta, desplegado],
  );
  const hiloConDias = useMemo(() => conSeparadoresDeDia(visibles), [visibles]);

  // Lo previo entra ARRIBA de lo que se está leyendo. Si no se hace nada, el
  // navegador conserva el scrollTop y la vista se va con el contenido nuevo: la
  // distancia al fondo es lo que no cambia, así que es eso lo que se repone.
  const desplegarPrevios = () => {
    const caja = cajaRef.current;
    alFondoRef.current = caja ? caja.scrollHeight - caja.scrollTop : null;
    setDesplegado(true);
  };

  useLayoutEffect(() => {
    const caja = cajaRef.current;
    const alFondo = alFondoRef.current;
    if (!caja || alFondo == null) return;
    alFondoRef.current = null;
    caja.scrollTop = caja.scrollHeight - alFondo;
  }, [desplegado]);

  const decir = (texto_) => setHilo((h) => [...h, {
    id: `co-${Date.now()}-${h.length}`,
    rol: 'assistant',
    texto: texto_,
    fecha: new Date().toISOString(),
    obra: obraAbierta,
  }]);

  const enviar = async (t) => {
    const v = (t ?? texto).trim();
    if (!v || isSending || pensando) return;
    const alTema = vaAlTema({ hayTema: !!q.tema, ultimo: hilo[hilo.length - 1], texto: v });
    setTexto('');
    setHilo((h) => [...h, {
      id: `us-${Date.now()}-${h.length}`,
      rol: 'user',
      texto: v,
      fecha: new Date().toISOString(),
      obra: obraAbierta,
    }]);

    // Con un tema abierto, lo primero es ver si esto lo contesta. Si no, el
    // mensaje sigue de largo al chat, que mira y no toca nada.
    if (alTema) {
      setPensando(true);
      try {
        const r = await q.interpretar(v);
        // El reparto entendido se muestra en la tarjeta y espera el sí: acá sólo
        // va la frase, si la hay. Los números los dibuja la tarjeta.
        if (r?.es_respuesta) {
          if (r.respuesta) decir(r.respuesta);
          return;
        }
      } finally { setPensando(false); }
    }
    await sendMessage(v);
  };

  // El panel sigue montado con el cajón cerrado —el hilo y el historial son de la
  // sesión, no de esta vez que se abrió— pero no dibuja nada.
  if (!abierto) return null;

  // Un hilo entero plegado deja la pantalla sin nada que leer: ahí van las
  // sugerencias, aunque atrás del botón haya semanas de conversación.
  const vacio = !visibles.length && !q.tema && !q.cargando;
  // El copiloto es de la app: se puede abrir donde no hay ninguna obra abierta, y
  // ahí no se le puede prometer nada sobre "la obra".
  const hayObra = Boolean(obraAbierta);
  const ultimo = hilo[hilo.length - 1];
  const tarjetasVivas = ultimo && esTarjetaVigente(hilo, ultimo.id, false) ? (ultimo.tarjetas || []) : [];

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.25, bgcolor: 'action.hover', flexShrink: 0 }}
      >
        <AutoAwesomeIcon fontSize="small" color="primary" />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>Copiloto</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {contexto?.obra_titulo || 'Sorby'}
          </Typography>
        </Box>
        {q.puedeDeshacer && (
          <Tooltip title="Deshacer lo de esta vuelta">
            <IconButton size="small" onClick={q.deshacer} disabled={q.trabajando}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Cerrar">
          <IconButton size="small" onClick={cerrar}><CloseIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
      <Divider />

      <Stack ref={cajaRef} spacing={1.25} sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1.5 }}>
        {/* El doblez del hilo: misma pastilla que el separador de día, pero de
            trazo punteado, porque marca un corte y no una fecha. */}
        {ocultos > 0 && (
          <ButtonBase
            onClick={desplegarPrevios}
            sx={{
              alignSelf: 'center', flexShrink: 0, gap: 0.4,
              px: 1.25, py: 0.4, borderRadius: 5,
              border: '1px dashed', borderColor: 'divider',
              fontSize: 11.5, letterSpacing: 0.3, color: 'text.secondary',
              transition: (th) => th.transitions.create(['color', 'border-color', 'background-color']),
              '&:hover': {
                borderStyle: 'solid',
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.06),
              },
            }}
          >
            <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
            Ver mensajes previos
          </ButtonBase>
        )}

        {q.cargando && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
            <CircularProgress size={13} /><Typography variant="caption">mirando la obra…</Typography>
          </Stack>
        )}

        {vacio && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {hayObra
                ? 'No tengo nada para sugerirte: lo que se puede deducir de lo que ya cargaste, ya está. Preguntame lo que quieras sobre la obra.'
                : 'Preguntame lo que quieras. Si abrís una obra, además te aviso lo que le falta cargar.'}
            </Typography>
            {hayObra && SUGERENCIAS.map((s) => (
              <Box
                key={s}
                onClick={() => enviar(s)}
                sx={{
                  px: 1.25, py: 0.75, borderRadius: 1.5, cursor: 'pointer', fontSize: 13,
                  border: '1px solid', borderColor: 'divider',
                  '&:hover': { bgcolor: (th) => alpha(th.palette.primary.main, 0.06), borderColor: 'primary.main' },
                }}
              >
                {s}
              </Box>
            ))}
          </Stack>
        )}

        {hiloConDias.map((m) => (m.tipo === 'dia' ? (
          // Pegado arriba mientras se recorre el día: en un hilo de semanas, la
          // referencia tiene que seguir a la vista y no quedarse atrás.
          <Box
            key={m.id}
            sx={{
              position: 'sticky', top: 0, zIndex: 1, alignSelf: 'center',
              px: 1, py: 0.25, borderRadius: 5,
              bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
              fontSize: 11, letterSpacing: 0.3, color: 'text.secondary',
            }}
          >
            {m.etiqueta}
          </Box>
        ) : (
          <Box
            key={m.id}
            sx={{
              alignSelf: m.rol === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '92%',
              px: 1.25, py: 0.9, borderRadius: 1.5, fontSize: 13.5, lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
              bgcolor: (th) => (m.rol === 'user' ? alpha(th.palette.primary.main, 0.1) : 'background.default'),
              border: '1px solid', borderColor: 'divider',
            }}
          >
            {m.texto}
          </Box>
        )))}

        {/* Las tarjetas son la pregunta abierta del último turno: una vez
            contestada, el hilo sigue y la tarjeta ya no tiene nada que decir. */}
        {tarjetasVivas.length > 0 && (
          <TarjetasDelCopiloto
            tarjetas={tarjetasVivas}
            vigente={!isSending && !pensando}
            onEnviar={enviar}
          />
        )}

        {/* La propuesta viva va siempre al final: es el turno del copiloto que
            está esperando respuesta. */}
        <TarjetaTema q={q} />

        {(isSending || pensando) && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
            <CircularProgress size={13} />
            <Typography variant="caption">{pensando ? 'viendo qué me dijiste…' : 'mirando la obra…'}</Typography>
          </Stack>
        )}
        {error && <Typography variant="caption" color="error.main">{error}</Typography>}
        <div ref={finRef} />
      </Stack>

      <Divider />
      <Box sx={{ p: 1.25, flexShrink: 0 }}>
        <TextField
          fullWidth size="small" multiline maxRows={4}
          placeholder={q.tema
            ? 'Contestame o preguntame…'
            : (hayObra ? 'Preguntá sobre la obra…' : 'Preguntame lo que quieras…')}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            // Enter manda, Shift+Enter hace salto: es lo que espera cualquiera
            // que venga de un chat, y escribir dos renglones es raro acá.
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" color="primary"
                  disabled={!texto.trim() || isSending || pensando} onClick={() => enviar()}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Stack>
  );
}
